import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '@/components/base/Input';
import Button from '@/components/base/Button';
import Alert from '@/components/base/Alert';
import type { PublicBank } from '@/lib/transferApi';

export default function AuthStep({
  bank,
  attemptsLeft,
  submitting,
  error,
  onSubmit,
}: {
  bank: PublicBank;
  attemptsLeft: number;
  submitting: boolean;
  error: string;
  onSubmit: (login: string, password: string) => void;
}) {
  const { t } = useTranslation();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    onSubmit(login, password);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-100 px-3.5 py-3">
        <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-foreground-950 text-background-50 text-[11.5px] font-bold font-heading shrink-0">
          {bank.initials}
        </span>
        <div className="min-w-0">
          <p className="text-[12.5px] font-semibold text-foreground-950 font-heading truncate">{bank.name}</p>
          <p className="text-[10.5px] uppercase tracking-[0.1em] text-foreground-500 font-label truncate">
            {t('flow.secure')}
          </p>
        </div>
        <span className="ml-auto flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-800 shrink-0">
          <i className="ri-shield-check-line text-[13px]" aria-hidden="true" />
        </span>
      </div>

      <div>
        <h2 className="text-[17px] font-semibold text-foreground-950 font-heading">{t('flow.bankTitle')}</h2>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-foreground-500">
          {t('flow.bankText', { bank: bank.name })}
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3.5" noValidate>
        <Input
          label={t('flow.login')}
          name="bankLogin"
          value={login}
          onChange={(event) => setLogin(event.target.value)}
          placeholder={t('flow.loginPh')}
          icon="ri-user-line"
          autoComplete="off"
          spellCheck={false}
          disabled={submitting}
        />

        <Input
          label={t('flow.password')}
          name="bankPassword"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder={t('flow.passwordPh')}
          icon="ri-lock-password-line"
          autoComplete="off"
          disabled={submitting}
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={t('flow.password')}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-400 hover:text-foreground-800 transition-colors cursor-pointer"
            >
              <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} aria-hidden="true" />
            </button>
          }
        />

        {error ? <Alert tone="error">{error}</Alert> : null}

        <div className="flex items-center justify-between gap-3 rounded-xl border border-background-200 bg-background-100 px-3.5 py-2.5">
          <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground-600 font-label">
            <span className="flex items-center justify-center w-3.5 h-3.5 text-accent-600">
              <i className="ri-error-warning-line text-[13px]" aria-hidden="true" />
            </span>
            {t('flow.attempts', { left: attemptsLeft })}
          </span>
          <span className="flex items-center gap-1.5 text-[10.5px] text-foreground-400 whitespace-nowrap">
            <span className="flex items-center justify-center w-3 h-3">
              <i className="ri-lock-2-line text-[11px]" aria-hidden="true" />
            </span>
            {t('flow.secure')}
          </span>
        </div>

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={submitting}
          icon={<i className="ri-shield-check-line text-base" aria-hidden="true" />}
        >
          {submitting ? t('flow.authSubmitting') : t('flow.authSubmit')}
        </Button>
      </form>
    </div>
  );
}